import { DOCUMENT } from '@angular/common';
import { effect, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { ContextService } from '@maximizer/core/shared/data-access';
import { InsightsService } from '@maximizer/core/shared/insights';
import {
  AUTH_CLEARED_MARKER_KEY,
  IS_BROWSER_ONLY_ROUTE,
  KeepAliveOutcome,
  OAuth2Service,
  performKeepAlive,
} from '@maximizer/oauth2/data-access';
import { normalizeError } from '@maximizer/shared/util';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { catchError, firstValueFrom, of, timeout } from 'rxjs';
import { OutlookInsightsEvents } from '../models';
import { OutlookStore } from '../state';

export type TokenMonitorNoticeState =
  | 'hidden'
  | 'refreshing'
  | 'continueRequired'
  | 'retryRequired';

export type TokenMonitorTrigger =
  | 'interval'
  | 'visibility'
  | 'continue'
  | 'retry';

@Injectable()
export class TokenMonitorService implements OnDestroy {
  private readonly oauth2Service = inject(OAuth2Service);
  private readonly context = inject(ContextService);
  private readonly outlookStore = inject(OutlookStore);
  private readonly insightsService = inject(InsightsService);
  private readonly document = inject(DOCUMENT, { optional: true });
  private readonly isBrowserOnlyRoute = inject(IS_BROWSER_ONLY_ROUTE);

  private readonly CHECK_INTERVAL_MS = 60_000;
  private readonly CHECK_INTERVAL_JITTER_MS = 5_000;
  private readonly SLEEP_GAP_THRESHOLD_MS = 3 * 60_000;
  private readonly REFRESH_TIMEOUT_MS = 15_000;
  private readonly KEEP_ALIVE_LOOKAHEAD_MS = 2 * 60_000;
  // Second attempt recovers a refresh consumed server-side but lost in transit
  // (the server replays a just-consumed refresh token within its grace window).
  private readonly MAX_REFRESH_ATTEMPTS = 2;
  private readonly RETRYABLE_REFRESH_FAILURES = [
    'NETWORK_ERROR',
    'NETWORK_ERROR_TOKEN_EXPIRED',
    'SERVER_ERROR',
  ];

  private checkTimer?: ReturnType<typeof setInterval>;
  private lastCheckAtMs = 0;
  private refreshInFlight = false;
  private checkInFlight = false;

  readonly noticeState = signal<TokenMonitorNoticeState>('hidden');
  readonly terminalFailure = signal<string | null>(null);

  private readonly handleVisibilityChange = (): void => {
    if (this.document?.visibilityState === 'visible') {
      void this.checkNow('visibility');
    }
  };

  constructor() {
    effect(() => {
      if (this.outlookStore.isAuthorized()) {
        this.startMonitoring();
      } else {
        this.stopMonitoring();
      }
    });

    if (!this.isBrowserOnlyRoute) {
      this.document?.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange,
      );
    }
  }

  ngOnDestroy(): void {
    this.document?.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
    );
    this.stopMonitoring();
  }

  continueRefresh(): Promise<void> {
    return this.checkNow('continue');
  }

  retryRefresh(): Promise<void> {
    return this.checkNow('retry');
  }

  clearTerminalFailure(): void {
    this.terminalFailure.set(null);
  }

  async checkNow(trigger: TokenMonitorTrigger): Promise<void> {
    const now = Date.now();
    const gapMs = this.lastCheckAtMs > 0 ? now - this.lastCheckAtMs : 0;
    const sleptGap = gapMs > this.SLEEP_GAP_THRESHOLD_MS;
    this.lastCheckAtMs = now;

    if (
      this.isBrowserOnlyRoute ||
      !this.outlookStore.isAuthorized() ||
      this.refreshInFlight ||
      this.checkInFlight
    ) {
      return;
    }

    this.checkInFlight = true;
    try {
      if (sleptGap) {
        // A pre-sleep failure marker must not veto the wake-up refresh.
        sessionStorage.removeItem(AUTH_CLEARED_MARKER_KEY);
        this.trackTrace('token-monitor-sleep-wake-detected', {
          trigger,
          gapMs,
        });
      }

      const isUserGesture = trigger === 'continue' || trigger === 'retry';

      if (this.noticeState() === 'continueRequired' && !isUserGesture) {
        await this.selfHealFromStorage();
        return;
      }

      if (this.noticeState() === 'retryRequired' && trigger === 'interval') {
        await this.selfHealFromStorage();
        return;
      }

      if (this.isTokenHealthy(now)) {
        this.noticeState.set('hidden');
        return;
      }

      const storedToken = await this.oauth2Service.syncTokenFromStorage({
        skipRefresh: true,
      });
      if (storedToken && this.isTokenHealthy(now)) {
        this.trackTrace('token-monitor-adopted-concurrent-refresh', {
          trigger,
        });
        this.noticeState.set('hidden');
        return;
      }

      const keepAliveOutcome = await this.pingTokenValid(trigger);
      if (keepAliveOutcome === 'kept-alive') {
        // The ping slid the server's validity window — no rotation needed.
        this.oauth2Service.recordAuthenticatedUse();
        this.noticeState.set('hidden');
        return;
      }
      if (sleptGap && !isUserGesture) {
        this.trackTrace('token-monitor-continue-required', { trigger, gapMs });
        this.noticeState.set('continueRequired');
        return;
      }

      if (keepAliveOutcome === 'transient' && !isUserGesture) {
        // Indeterminate — leave state unchanged and re-check next tick.
        return;
      }

      await this.refreshWithNotice(trigger);
    } finally {
      this.checkInFlight = false;
    }
  }

  private async selfHealFromStorage(): Promise<void> {
    await this.oauth2Service.syncTokenFromStorage({ skipRefresh: true });
    if (this.oauth2Service.isAccessTokenEffectivelyValid()) {
      this.noticeState.set('hidden');
    }
  }

  private isTokenHealthy(nowMs: number): boolean {
    return this.oauth2Service.isAccessTokenEffectivelyValid(
      nowMs + this.KEEP_ALIVE_LOOKAHEAD_MS,
    );
  }

  private async pingTokenValid(
    trigger: TokenMonitorTrigger,
  ): Promise<KeepAliveOutcome | 'skipped'> {
    const apiEndpoint = this.context.api;
    const accessToken =
      this.oauth2Service.oAuth2Token?.access_token ?? this.context.token;
    if (!apiEndpoint || !accessToken) {
      // No endpoint/bearer to ping — let the refresh flow handle it.
      return 'skipped';
    }

    const outcome = await performKeepAlive(apiEndpoint, accessToken);
    this.trackTrace('token-monitor-keep-alive', { trigger, outcome });
    return outcome;
  }

  private startMonitoring(): void {
    if (this.isBrowserOnlyRoute || this.checkTimer) {
      return;
    }

    this.lastCheckAtMs = Date.now();

    // Jitter desynchronizes checks across concurrently open panes.
    const jitterMs = Math.round(
      (Math.random() * 2 - 1) * this.CHECK_INTERVAL_JITTER_MS,
    );
    this.checkTimer = setInterval(() => {
      void this.checkNow('interval');
    }, this.CHECK_INTERVAL_MS + jitterMs);
  }

  private stopMonitoring(): void {
    if (this.checkTimer) {
      clearInterval(this.checkTimer);
      this.checkTimer = undefined;
    }
    this.lastCheckAtMs = 0;
    this.noticeState.set('hidden');
  }

  private async refreshWithNotice(trigger: TokenMonitorTrigger): Promise<void> {
    this.noticeState.set('refreshing');
    this.refreshInFlight = true;
    this.trackTrace('token-monitor-refresh-start', { trigger });

    try {
      for (let attempt = 1; attempt <= this.MAX_REFRESH_ATTEMPTS; attempt++) {
        const token = await firstValueFrom(
          this.oauth2Service
            .refreshToken(undefined, true, 'token-monitor')
            .pipe(
              timeout(this.REFRESH_TIMEOUT_MS),
              catchError(() => of(null)),
            ),
          { defaultValue: null },
        );

        if (token) {
          this.trackTrace('token-monitor-refresh-success', {
            trigger,
            attempt,
          });
          this.noticeState.set('hidden');
          return;
        }

        const failureReason = this.oauth2Service.getLastRefreshFailureReason();
        const isRetryable =
          !failureReason ||
          this.RETRYABLE_REFRESH_FAILURES.includes(failureReason);

        if (!isRetryable) {
          this.trackException(
            'token-monitor-refresh-terminal-failure',
            new Error(`Token refresh failed terminally: ${failureReason}`),
            { trigger, failureReason },
          );
          this.noticeState.set('hidden');
          this.terminalFailure.set('session-failed');
          return;
        }

        this.trackTrace('token-monitor-refresh-retryable-failure', {
          trigger,
          failureReason,
          attempt,
        });
      }

      this.noticeState.set('retryRequired');
    } finally {
      this.refreshInFlight = false;
    }
  }

  private trackTrace(
    message: string,
    metadata?: Record<string, unknown>,
  ): void {
    this.insightsService.trackTrace(
      { message, severityLevel: SeverityLevel.Verbose },
      {
        component: 'TokenMonitorService',
        eventId: OutlookInsightsEvents.OutlookAuth,
        noticeState: this.noticeState(),
        ...metadata,
      },
    );
  }

  private trackException(
    event: string,
    error: unknown,
    metadata?: Record<string, unknown>,
  ): void {
    const errorMessage = normalizeError(error);

    this.insightsService.trackException(
      { error: error instanceof Error ? error : new Error(errorMessage) },
      {
        component: 'TokenMonitorService',
        eventId: OutlookInsightsEvents.OutlookAuth,
        event,
        errorMessage,
        noticeState: this.noticeState(),
        ...metadata,
      },
    );
  }
}
